import React, { Dispatch, SetStateAction } from 'react'
import {
  Badge,
  Button,
  CheckboxGroup,
  Collapse,
  Panel,
  theme
} from '@lmc/ui-kit'
import styled from 'styled-components'
import { Carer, UserRole } from '@lmc/ts-common'
import { getCarerRole } from '../utils'
import { Home } from '@lmc/ts-common'
import { useSelector } from 'react-redux'
import { commonPatterns } from '../../../../../../src/utils/enhancedTestIds'

type CheckboxValueType = string | number | boolean

export type TeamSideFiltersGroupsProps = {
  carer?: Carer
  onClick?: () => void
  onStatuses: Dispatch<SetStateAction<string[]>>
  onRoles: Dispatch<SetStateAction<UserRole[]>>
  onSites: Dispatch<SetStateAction<string[]>>
  statuses: string[]
  roles: string[]
  features: Home
  sites: string[]
  clearButtonTitle: string
  statusTitle?: string
  roleTitle: string
  siteTitle: string
  onDefaultOpened?: boolean
  viewProTitle?: string
  onSubscribe?: () => void
  isGroupUser?: boolean
  testId?: string
}

const StyledCollapse = styled(Collapse)`
  background-color: ${(props) => props.theme.color.neutral.color10} !important;
  border-radius: 8px;
`

const StyledExtraWrapper = styled.div`
  display: flex;
`

const StyledBadge = styled(Badge)`
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background-color: red;
`

const StyledWrapper = styled.div`
  max-width: 345px;
  width: 100%;
  margin-top: 24px;
  padding-left: 24px;
  overflow-y: auto;
  height: calc(100vh - 210px);
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-bottom: 24px;
    max-width: unset;
    padding-right: 24px;
  }
`

const StyledCheckboxGroup = styled(CheckboxGroup)`
  display: flex !important;
  flex-direction: column !important;

  .ant-checkbox-wrapper {
    margin-left: 0px;
  }
`

const TeamSideGroupsFilters: React.FC<TeamSideFiltersGroupsProps> = ({
  onStatuses,
  onRoles,
  onSites,
  statuses,
  roles,
  sites,
  clearButtonTitle,
  statusTitle,
  roleTitle,
  siteTitle,
  isGroupUser,
  testId
}) => {
  const onChangeStatus = (checkedValues: CheckboxValueType[]) =>
    onStatuses(checkedValues as string[])

  const onChangeRole = (checkedValues: CheckboxValueType[]) =>
    onRoles(checkedValues as UserRole[])

  const onChangeSites = (checkedValues: CheckboxValueType[]) =>
    onSites(checkedValues as string[])

  const getExtra = (
    values: string[],
    onClear: Dispatch<SetStateAction<string[]>>
  ) => {
    const hasValues = !!values?.length

    return (
      <StyledExtraWrapper>
        {hasValues && (
          <Button
            variant="text"
            onClick={(e) => {
              e.stopPropagation()
              onClear([])
            }}
            data-testid={
              testId ? commonPatterns.button(testId, 'clear') : undefined
            }
          >
            {clearButtonTitle}
          </Button>
        )}
        <StyledBadge
          size="small"
          text={values?.length.toString()}
          color={
            hasValues ? theme.color.status.primary : theme.color.neutral.color8
          }
          textColor={
            !hasValues
              ? theme.color.neutral.color1
              : theme.color.neutral.color11
          }
          style={{
            height: 24,
            width: 24,
            padding: '4px 0px 0px 9.5px',
            marginLeft: 5,
            paddingRight: 5
          }}
        />
      </StyledExtraWrapper>
    )
  }

  const homes = useSelector(
    (state: { data: { homes: Home[] } }) => state.data.homes
  )

  const homeOptions = homes?.map((home) => ({
    value: home?.id,
    label: home?.name
  }))

  let roleOptions = [
    {
      value: 'carehome-admin',
      label: getCarerRole(UserRole.careHomeAdmin)
    },
    { value: 'carer', label: getCarerRole(UserRole.carer) },
    {
      value: 'senior-carer',
      label: getCarerRole(UserRole.seniorCarer)
    }
  ]

  if (isGroupUser) {
    roleOptions = [
      ...roleOptions,
      {
        value: UserRole.groupAdmin,
        label: getCarerRole(UserRole.groupAdmin)
      },
      {
        value: UserRole.groupManager,
        label: getCarerRole(UserRole.groupManager)
      }
    ]
  }

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ]

  return (
    <StyledWrapper data-testid={testId}>
      <StyledCollapse
        expandIconPosition="start"
        defaultActiveKey={['1']}
        data-testid={testId ? `${testId}-collapse` : undefined}
      >
        <Panel
          header={statusTitle}
          key="1"
          extra={getExtra(statuses, onStatuses)}
          data-testid={testId ? `${testId}-status-panel` : undefined}
        >
          <StyledCheckboxGroup
            onChange={onChangeStatus}
            options={statusOptions}
            value={statuses}
            data-testid={testId ? `${testId}-status-checkboxes` : undefined}
          />
        </Panel>
        <Panel
          header={roleTitle}
          key="2"
          extra={getExtra(
            roles,
            onRoles as unknown as Dispatch<SetStateAction<string[]>>
          )}
          data-testid={testId ? `${testId}-role-panel` : undefined}
        >
          <StyledCheckboxGroup
            onChange={onChangeRole}
            value={roles}
            options={roleOptions}
            data-testid={testId ? `${testId}-role-checkboxes` : undefined}
          />
        </Panel>
        {isGroupUser && (
          <Panel
            header={siteTitle}
            key="3"
            extra={getExtra(sites, onSites)}
            data-testid={testId ? `${testId}-sites-panel` : undefined}
          >
            <StyledCheckboxGroup
              onChange={onChangeSites}
              value={sites}
              options={homeOptions}
              data-testid={testId ? `${testId}-sites-checkboxes` : undefined}
            />
          </Panel>
        )}
      </StyledCollapse>
    </StyledWrapper>
  )
}

export default TeamSideGroupsFilters
